/**
 * AssumptionList — the "view" tab: filter bar (template + year) over a list of
 * file cards. Selecting a card opens it in the editing grid.
 *
 * Ported to TSX + Nordic; framer-motion removed (cards use the `view-in`
 * entrance + CSS hover transitions).
 */
import { useMemo, useState } from "react";
import { Calendar, ChevronRight, Database, FileSpreadsheet, Filter } from "lucide-react";

import type {
  AssumptionFileItem,
  FileTemplateMappings,
  TemplateItem,
} from "./types";

export interface AssumptionListProps {
  items: AssumptionFileItem[];
  onSelect: (item: AssumptionFileItem) => void;
  templates?: TemplateItem[];
  fileTemplateMappings?: FileTemplateMappings | null;
}

export default function AssumptionList({
  items,
  onSelect,
  templates = [],
  fileTemplateMappings = null,
}: AssumptionListProps) {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  const mappings = fileTemplateMappings?.mappings || {};

  // Mappings are keyed by full S3 key (backend convention); items carry the
  // S3 key as `id`. Look up by id, falling back to a filename-suffix match.
  const lookupMapping = (item: AssumptionFileItem) => {
    if (mappings[item.id]) return mappings[item.id];
    const found = Object.entries(mappings).find(
      ([k]) => k.endsWith("/" + item.name) || k === item.name,
    );
    return found ? found[1] : undefined;
  };

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    Object.values(mappings).forEach((mapping) => {
      if (mapping.uploaded_at) {
        years.add(new Date(mapping.uploaded_at).getFullYear());
      }
    });
    (items || []).forEach((item) => {
      if (item.date) years.add(new Date(item.date).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [mappings, items]);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    const filtered = items.filter((item) => {
      const mapping = lookupMapping(item);
      if (selectedTemplate) {
        if (!mapping || mapping.template_id !== selectedTemplate) return false;
      }
      if (selectedYear) {
        const yearNum = parseInt(selectedYear, 10);
        const dateStr = mapping?.uploaded_at || item.date;
        if (dateStr) {
          if (new Date(dateStr).getFullYear() !== yearNum) return false;
        } else {
          return false;
        }
      }
      return true;
    });
    filtered.sort((a, b) => {
      const dateA = new Date(lookupMapping(a)?.uploaded_at || a.date || 0);
      const dateB = new Date(lookupMapping(b)?.uploaded_at || b.date || 0);
      return dateB.getTime() - dateA.getTime();
    });
    return filtered;
  }, [items, mappings, selectedTemplate, selectedYear]);

  const templateOptions = useMemo(
    () =>
      templates.map((t) => ({
        value: t.name.replace(".json", ""),
        label: t.displayName || t.name,
      })),
    [templates],
  );

  const selectClass =
    "w-full px-3 py-2 text-[13px] font-serif rounded border hairline t-base focus-ring outline-none";
  const labelClass =
    "block text-[10px] font-mono uppercase tracking-[0.14em] mb-1";

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div
        className="p-4 rounded-lg border hairline"
        style={{ background: "var(--bg-elev)" }}
      >
        <div
          className="flex items-center gap-2 mb-3 text-[10px] font-mono uppercase tracking-[0.14em]"
          style={{ color: "var(--ink-3)" }}
        >
          <Filter size={14} strokeWidth={1.5} />
          Filters
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className={labelClass} style={{ color: "var(--ink-3)" }}>
              Template
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className={selectClass}
              style={{ background: "var(--bg)", color: "var(--ink)" }}
            >
              <option value="">All Templates</option>
              {templateOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className={labelClass} style={{ color: "var(--ink-3)" }}>
              Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className={selectClass}
              style={{ background: "var(--bg)", color: "var(--ink)" }}
            >
              <option value="">All Years</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {(selectedTemplate || selectedYear) && (
        <p
          className="text-[12px] font-serif px-1"
          style={{ color: "var(--ink-3)" }}
        >
          Showing {filteredItems.length} of {items?.length || 0} files
        </p>
      )}

      {filteredItems.length === 0 ? (
        <div
          className="text-center py-12"
          style={{ color: "var(--ink-3)" }}
        >
          <Database
            size={40}
            strokeWidth={1.25}
            className="mx-auto mb-3"
            style={{ color: "var(--ink-4)" }}
          />
          <p className="font-serif text-[14px]">
            {items?.length > 0
              ? "No files match the selected filters."
              : "No assumptions found."}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredItems.map((item) => {
            const mapping = lookupMapping(item);
            return (
              <button
                key={item.id}
                onClick={() => onSelect(item)}
                className="text-left p-4 rounded-lg border hairline t-base hover:shadow-sm focus-ring group view-in"
                style={{ background: "var(--bg-elev)" }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className="p-3 rounded-lg t-base"
                      style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
                    >
                      <FileSpreadsheet size={22} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3
                        className="font-serif text-[17px] leading-tight"
                        style={{ color: "var(--ink)" }}
                      >
                        {item.name}
                      </h3>
                      <div
                        className="flex items-center gap-3 text-[12px] font-serif mt-1 flex-wrap"
                        style={{ color: "var(--ink-3)" }}
                      >
                        <span className="flex items-center gap-1">
                          <Calendar size={12} strokeWidth={1.5} />
                          Last Modified: {item.date}
                        </span>
                        <span>•</span>
                        <span>{item.size} KB</span>
                        {mapping && (
                          <>
                            <span>•</span>
                            <span
                              className="px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-[0.1em]"
                              style={{
                                background: "var(--hover)",
                                color: "var(--ink-2)",
                              }}
                            >
                              {mapping.template_id.replace(/_/g, " ")}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight
                    size={18}
                    strokeWidth={1.5}
                    className="t-base group-hover:translate-x-1"
                    style={{ color: "var(--ink-3)" }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
